import { isPreviewExperimentOverrideHost } from '../utils/experimentOverrideHosts';
import { useState, useEffect, useRef } from 'react';
import { capturePostHogEvent, POSTHOG_KEY, pushEvent } from '../utils/analytics';
import {
  safeGetStorageItem,
  safeSetStorageItem,
  safeRemoveStorageItem,
} from '../utils/browserStorage';
import {
  EXPERIMENT_PREVIEW_OVERRIDE_EVENT,
  getExperimentPreviewOverride,
} from '../utils/experimentPreviewOverrides';
import { watchExperimentFlags } from '../utils/experimentFlagUpdates';
import { shouldHoldExperimentBaseline } from '../utils/experimentAutomationBaseline';

const POSTHOG_READY_TIMEOUT_MS = 5000;
const FEATURE_FLAGS_READY_TIMEOUT_MS = 3000;

const getPostHogFeatureFlagsClient = () => {
  if (typeof window === 'undefined' || !window.posthog) {
    return null;
  }

  return window.posthog.featureFlags || window.posthog;
};

const getPostHogFeatureFlagsListener = () => {
  if (typeof window === 'undefined' || !window.posthog) {
    return null;
  }

  if (typeof window.posthog.onFeatureFlags === 'function') {
    return window.posthog.onFeatureFlags.bind(window.posthog);
  }

  const featureFlagsClient = getPostHogFeatureFlagsClient();
  if (featureFlagsClient && typeof featureFlagsClient.onFeatureFlags === 'function') {
    return featureFlagsClient.onFeatureFlags.bind(featureFlagsClient);
  }

  return null;
};

const hasLoadedPostHogFeatureFlags = () => {
  const featureFlagsClient = getPostHogFeatureFlagsClient();

  if (!featureFlagsClient) {
    return false;
  }

  if (featureFlagsClient._hasLoadedFlags === true) {
    return true;
  }

  if (typeof featureFlagsClient.getFlags === 'function') {
    const flags = featureFlagsClient.getFlags();
    if (Array.isArray(flags) && flags.length > 0) {
      return true;
    }
  }

  if (typeof featureFlagsClient.getFlagVariants === 'function') {
    const flagVariants = featureFlagsClient.getFlagVariants();
    if (flagVariants && typeof flagVariants === 'object' && Object.keys(flagVariants).length > 0) {
      return true;
    }
  }

  return false;
};

const waitForPostHog = (experimentName, timeoutMs = POSTHOG_READY_TIMEOUT_MS) =>
  new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }

    let settled = false;
    let timeoutId;
    let pollId;

    const settle = (isReady) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeoutId);
      clearTimeout(pollId);
      resolve(isReady);
    };

    const checkPostHog = () => {
      if (window.posthog && typeof window.posthog.getFeatureFlag === 'function') {
        console.debug(`🎯 [A/B Test] PostHog ready for ${experimentName}`);
        settle(true);
        return;
      }

      console.debug(`⏳ [A/B Test] Waiting for PostHog to load for ${experimentName}...`);
      pollId = setTimeout(checkPostHog, 100);
    };

    timeoutId = setTimeout(() => settle(false), timeoutMs);
    checkPostHog();
  });

const waitForPostHogFeatureFlags = (featureFlagKey, timeoutMs = FEATURE_FLAGS_READY_TIMEOUT_MS) =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.posthog) {
      resolve(false);
      return;
    }

    if (hasLoadedPostHogFeatureFlags()) {
      console.debug(`🎯 [A/B Test] Feature flags already loaded for ${featureFlagKey}`);
      resolve(true);
      return;
    }

    const onFeatureFlags = getPostHogFeatureFlagsListener();
    if (!onFeatureFlags) {
      resolve(false);
      return;
    }

    let settled = false;
    let unsubscribe;
    let timeoutId;

    const settle = (didLoadFlags) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeoutId);

      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }

      resolve(didLoadFlags);
    };

    timeoutId = setTimeout(() => settle(false), timeoutMs);

    try {
      unsubscribe = onFeatureFlags(() => {
        console.debug(`🎯 [A/B Test] Feature flags loaded for ${featureFlagKey}`);
        settle(true);
      });

      if (settled && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    } catch (error) {
      console.warn(`PostHog feature flags listener failed for ${featureFlagKey}:`, error);
      settle(false);
    }
  });

const isActiveFeatureFlagValue = (value) =>
  value !== false && value !== null && value !== undefined;

const isValidStickyAssignment = (assignment, validVariants) => {
  const variant = assignment?.variant;
  const exposureFlagValue = assignment?.exposureFlagValue;
  return (
    typeof variant === 'string' &&
    variant.length > 0 &&
    (!Array.isArray(validVariants) || validVariants.includes(variant)) &&
    (exposureFlagValue === variant ||
      (exposureFlagValue === true && variant === 'test') ||
      (exposureFlagValue === false && variant === 'control'))
  );
};

/** @returns {{ variant: string, exposureFlagValue: string | boolean } | null} */
export const readStickyExperimentAssignment = (featureFlagKey, validVariants, validFlagValues) => {
  const storageKey = `botspot:abtest:sticky:${featureFlagKey}`;
  try {
    const assignment = JSON.parse(safeGetStorageItem('localStorage', storageKey));
    return isValidStickyAssignment(assignment, validVariants) &&
      (!Array.isArray(validFlagValues) || validFlagValues.includes(assignment.exposureFlagValue))
      ? assignment
      : null;
  } catch {
    return null;
  }
};

const getPostHogFlagCaptureProperties = (featureFlagKey, exposureFlagValue) => {
  if (typeof window === 'undefined' || !window.posthog) {
    return {};
  }

  const posthogProps = window.posthog?.persistence?.props || {};
  const flagDetails = posthogProps.$feature_flag_details?.[featureFlagKey] || {};
  const flagMetadata = flagDetails.metadata || {};
  const flagReason = flagDetails.reason || {};
  const enabledFeatureFlags = {
    ...(posthogProps.$enabled_feature_flags &&
    typeof posthogProps.$enabled_feature_flags === 'object' &&
    !Array.isArray(posthogProps.$enabled_feature_flags)
      ? posthogProps.$enabled_feature_flags
      : {}),
  };
  const activeFeatureFlags = Array.isArray(posthogProps.$active_feature_flags)
    ? [...posthogProps.$active_feature_flags]
    : [];

  if (isActiveFeatureFlagValue(exposureFlagValue)) {
    enabledFeatureFlags[featureFlagKey] = exposureFlagValue;
    if (!activeFeatureFlags.includes(featureFlagKey)) {
      activeFeatureFlags.push(featureFlagKey);
    }
  }

  return {
    [`$feature/${featureFlagKey}`]: exposureFlagValue,
    ...(activeFeatureFlags.length > 0 && { $active_feature_flags: activeFeatureFlags }),
    ...(Object.keys(enabledFeatureFlags).length > 0 && {
      $enabled_feature_flags: enabledFeatureFlags,
    }),
    ...(flagMetadata.id && { $feature_flag_id: flagMetadata.id }),
    ...(flagMetadata.version && { $feature_flag_version: flagMetadata.version }),
    ...(flagReason.description && { $feature_flag_reason: flagReason.description }),
    ...(posthogProps.$feature_flag_request_id && {
      $feature_flag_request_id: posthogProps.$feature_flag_request_id,
    }),
    ...(posthogProps.$feature_flag_payloads && {
      $feature_flag_payloads: posthogProps.$feature_flag_payloads,
    }),
  };
};

const trackExperimentExposure = ({
  experimentName,
  featureFlagKey,
  selectedVariant,
  exposureFlagValue,
  exposureIsActive,
  eventTrackingData,
}) => {
  const posthogFlagProperties = getPostHogFlagCaptureProperties(featureFlagKey, exposureFlagValue);

  capturePostHogEvent('$feature_flag_called', {
    $feature_flag: featureFlagKey,
    $feature_flag_response: exposureFlagValue,
    experiment_name: experimentName,
    variant: selectedVariant,
    is_experiment_active: exposureIsActive,
    ...posthogFlagProperties,
    ...eventTrackingData,
  });

  // Also track our custom experiment exposure event
  capturePostHogEvent('experiment_exposure', {
    experiment_name: experimentName,
    variant: selectedVariant,
    feature_flag: featureFlagKey,
    feature_flag_value: exposureFlagValue,
    is_experiment_active: exposureIsActive,
    ...posthogFlagProperties,
    ...eventTrackingData,
  });
};

/**
 * useABTest - Generic A/B Testing Hook
 *
 * This hook can be used anywhere in the app to run A/B tests using PostHog feature flags.
 * It automatically tracks experiment exposure and provides the variant information.
 *
 * @param {string} experimentName - Name of the experiment (for tracking)
 * @param {string} featureFlagKey - PostHog feature flag key
 * @param {*} defaultVariant - Default variant if flag fails (usually 'control')
 * @param {Object} trackingData - Additional data to track with the experiment.
 *   On localhost and Dev an automated browser that names no arm gets
 *   defaultVariant without a flag read (see utils/experimentAutomationBaseline).
 *   Pass allowAutomatedLiveAssignment: true only for an explicit QA URL.
 * @returns {{ variant: string | boolean | null, flagValue: string | boolean | null, isLoading: boolean, isExperimentActive: boolean, isPreviewOverride: boolean, isControl: boolean, isTest: string | boolean | null, isVariant: string | boolean | null, isA: boolean, isB: string | boolean | null }}
 */
const useABTest = (
  experimentName,
  featureFlagKey,
  defaultVariant = 'control',
  trackingData = {},
) => {
  const [variant, setVariant] = useState(null);
  const [flagValue, setFlagValue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExperimentActive, setIsExperimentActive] = useState(false);
  const [isPreviewOverride, setIsPreviewOverride] = useState(false);
  const [, setPreviewOverrideRevision] = useState(0);
  const trackingDataRef = useRef(trackingData);
  trackingDataRef.current = trackingData;
  const callerHasForcedVariant = Boolean(
    trackingData?.skipFeatureFlagEvaluation &&
    trackingData?.forcedVariant !== null &&
    trackingData?.forcedVariant !== undefined,
  );
  const settingsPreviewOverride = callerHasForcedVariant
    ? null
    : getExperimentPreviewOverride(featureFlagKey, trackingData?.validVariants);
  const trackingConfigKey = JSON.stringify({
    skipFeatureFlagEvaluation: trackingData?.skipFeatureFlagEvaluation,
    forcedVariant: trackingData?.forcedVariant,
    forcedExposureValue: trackingData?.forcedExposureValue,
    trackForcedExposure: trackingData?.trackForcedExposure,
    trackExposure: trackingData?.trackExposure,
    normalizeControlExposure: trackingData?.normalizeControlExposure,
    validVariants: trackingData?.validVariants,
    validFlagValues: trackingData?.validFlagValues,
    allowAutomatedLiveAssignment: trackingData?.allowAutomatedLiveAssignment,
    settingsPreviewOverride,
  });

  useEffect(() => {
    const browserWindow = window;

    const handlePreviewOverrideChange = (event) => {
      const changedFeatureFlag = event?.detail?.featureFlagKey;
      if (!changedFeatureFlag || changedFeatureFlag === featureFlagKey) {
        setPreviewOverrideRevision((revision) => revision + 1);
      }
    };

    browserWindow.addEventListener(EXPERIMENT_PREVIEW_OVERRIDE_EVENT, handlePreviewOverrideChange);
    return () => {
      browserWindow.removeEventListener(
        EXPERIMENT_PREVIEW_OVERRIDE_EVENT,
        handlePreviewOverrideChange,
      );
    };
  }, [featureFlagKey]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe;
    let revision = 0;

    const runExperiment = async () => {
      setIsLoading(true);
      const {
        skipFeatureFlagEvaluation,
        forcedVariant,
        forcedExposureValue,
        trackForcedExposure,
        trackExposure = true,
        normalizeControlExposure: _normalizeControlExposure,
        validVariants,
        validFlagValues,
        allowAutomatedLiveAssignment,
        ...eventTrackingData
      } = trackingDataRef.current || {};

      const previewHost = isPreviewExperimentOverrideHost();
      const hasCallerForcedVariant = Boolean(
        skipFeatureFlagEvaluation &&
        forcedVariant !== null &&
        forcedVariant !== undefined &&
        (previewHost || forcedVariant === defaultVariant),
      );
      const appliedPreviewOverride = hasCallerForcedVariant ? null : settingsPreviewOverride;
      const appliedForcedVariant = hasCallerForcedVariant ? forcedVariant : appliedPreviewOverride;

      if (appliedForcedVariant !== null && appliedForcedVariant !== undefined) {
        setVariant(appliedForcedVariant);
        setFlagValue(previewHost ? appliedForcedVariant : null);
        setIsExperimentActive(previewHost);
        setIsPreviewOverride(Boolean(appliedPreviewOverride));
        if (
          previewHost &&
          hasCallerForcedVariant &&
          trackForcedExposure &&
          trackExposure &&
          POSTHOG_KEY
        ) {
          void (async () => {
            try {
              await waitForPostHog(experimentName);
              await waitForPostHogFeatureFlags(featureFlagKey);
              if (cancelled) return;
              const exposureFlagValue = forcedExposureValue ?? appliedForcedVariant;
              trackExperimentExposure({
                experimentName,
                featureFlagKey,
                selectedVariant: appliedForcedVariant,
                exposureFlagValue,
                exposureIsActive: isActiveFeatureFlagValue(exposureFlagValue),
                eventTrackingData,
              });
            } catch (error) {
              console.warn(`PostHog forced exposure tracking failed for ${featureFlagKey}:`, error);
            }
          })();
        }
        setIsLoading(false);
        return;
      }

      // Localhost, Dev and production share one PostHog project, so a live
      // experiment would bucket every release spec that names no arm at random
      // (2026-09-24: 23, then 58 failures). An automated browser on a preview
      // host keeps the baseline arm: no flag read, no exposure, no sticky write.
      // Explicit arms were applied above; production hosts never reach here.
      if (!allowAutomatedLiveAssignment && shouldHoldExperimentBaseline(featureFlagKey)) {
        setVariant(defaultVariant);
        setFlagValue(null);
        setIsExperimentActive(false);
        setIsPreviewOverride(false);
        setIsLoading(false);
        return;
      }

      const stickyStorageKey = `botspot:abtest:sticky:${featureFlagKey}`;
      const reset = (rawValue = null) => {
        safeRemoveStorageItem('localStorage', stickyStorageKey);
        setVariant(defaultVariant);
        setFlagValue(rawValue ?? null);
        setIsExperimentActive(false);
        setIsPreviewOverride(false);
        setIsLoading(false);
      };
      if (!POSTHOG_KEY) {
        reset();
        return;
      }

      // Persistence deduplicates the current arm; PostHog owns every decision.
      // A newer flag snapshot always wins over an older asynchronous evaluation.
      const evaluate = async () => {
        const currentRevision = ++revision;
        try {
          const rawValue = await window.posthog?.getFeatureFlag(featureFlagKey, {
            send_event: false,
          });
          if (cancelled || currentRevision !== revision) return;
          const currentVariant = rawValue === true ? 'test' : rawValue;
          const active =
            isActiveFeatureFlagValue(rawValue) &&
            typeof currentVariant === 'string' &&
            (!Array.isArray(validVariants) || validVariants.includes(currentVariant)) &&
            (!Array.isArray(validFlagValues) || validFlagValues.includes(rawValue));
          if (!active) {
            reset(rawValue);
            return;
          }
          const cached = readStickyExperimentAssignment(
            featureFlagKey,
            validVariants,
            validFlagValues,
          );
          const assignment =
            cached && cached.variant === currentVariant && cached.exposureFlagValue === rawValue
              ? cached
              : { variant: currentVariant, exposureFlagValue: rawValue };
          if (!cached || assignment !== cached) {
            safeSetStorageItem('localStorage', stickyStorageKey, JSON.stringify(assignment));
          }
          setVariant(assignment.variant);
          setFlagValue(assignment.exposureFlagValue);
          setIsExperimentActive(true);
          setIsPreviewOverride(false);
          setIsLoading(false);
          if (trackExposure && assignment !== cached) {
            trackExperimentExposure({
              experimentName,
              featureFlagKey,
              selectedVariant: assignment.variant,
              exposureFlagValue: assignment.exposureFlagValue,
              exposureIsActive: true,
              eventTrackingData,
            });
          }
        } catch (error) {
          if (cancelled || currentRevision !== revision) return;
          reset();
          pushEvent({
            event: 'experiment_error',
            experiment_name: experimentName,
            variant: defaultVariant,
            feature_flag: featureFlagKey,
            error: 'feature_flag_error',
            error_message: error.message,
            ...eventTrackingData,
          });
        }
      };
      await waitForPostHog(experimentName);
      await waitForPostHogFeatureFlags(featureFlagKey);
      if (cancelled) return;
      unsubscribe = watchExperimentFlags(window.posthog, evaluate);
      await evaluate();
    };

    runExperiment();
    return () => {
      cancelled = true;
      revision += 1;
      unsubscribe?.();
    };
  }, [experimentName, featureFlagKey, defaultVariant, settingsPreviewOverride, trackingConfigKey]);

  return {
    variant,
    flagValue,
    isLoading,
    isExperimentActive,
    isPreviewOverride,
    // Helper functions
    isControl: variant === 'control' || variant === false || variant === null,
    isTest: variant && variant !== 'control' && variant !== false && variant !== null,
    isVariant: variant && variant !== 'control' && variant !== defaultVariant, // Legacy support
    // For boolean tests (PostHog experiments)
    isA: variant === 'control' || variant === false || variant === null,
    isB: variant && variant !== 'control' && variant !== false && variant !== null,
  };
};

export default useABTest;
